import { protectedStorefrontFetch } from './storefront-fetch.js';

const FEEDBACK_ENDPOINT = '/api/article-feedback';
const FEEDBACK_STORAGE_PREFIX = 'ippodo:article-feedback:';
const SOMETHING_ELSE = 'something_else';

function getStoredVote(articleId) {
  try {
    return window.localStorage.getItem(`${FEEDBACK_STORAGE_PREFIX}${articleId}`);
  } catch {
    return null;
  }
}

function storeVote(articleId, vote) {
  try {
    window.localStorage.setItem(`${FEEDBACK_STORAGE_PREFIX}${articleId}`, vote);
  } catch {
    // The server result remains authoritative when storage is unavailable.
  }
}

function focusElement(element) {
  try {
    element?.focus?.({ preventScroll: false });
  } catch {
    // Non-visual DOMs (tests) may not implement focus.
  }
}

function showThanks(container, message, options = {}) {
  const { focus = true } = options;
  const documentRoot = container.ownerDocument;
  container.replaceChildren();
  const thanks = documentRoot.createElement('p');
  thanks.className = 'article-feedback__thanks';
  thanks.setAttribute('tabindex', '-1');
  thanks.textContent = message;
  container.append(thanks);
  // Only steal focus after the visitor just interacted with the widget.
  // On page load (restoring a stored vote) focusing here scrolls returning
  // readers straight to the bottom of the article.
  if (focus) focusElement(thanks);
}

function setBusy(container, busy) {
  container.classList.toggle('loading', busy);
  if (busy) {
    container.setAttribute('aria-busy', 'true');
  } else {
    container.removeAttribute('aria-busy');
  }
}

function selectedReason(form) {
  const radios = form.querySelectorAll('input[name="feedback-reason"]');
  for (const radio of radios) {
    if (radio.checked) return radio.value;
  }
  return '';
}

export function initializeArticleFeedback(
  root = document,
  submitFeedback = protectedStorefrontFetch
) {
  const container = root.querySelector('#article-feedback');
  if (!container) return;

  const articleId = container.dataset.articleid;
  if (!articleId) return;

  const thanksMessage = container.dataset.thanksLabel || 'Thank you. We appreciate your feedback.';

  if (getStoredVote(articleId)) {
    showThanks(container, thanksMessage, { focus: false });
    return;
  }

  const prompt = container.querySelector('[data-feedback-prompt]');
  const yesButton = container.querySelector('[data-feedback-yes]');
  const noButton = container.querySelector('[data-feedback-no]');
  const form = container.querySelector('#article-feedback-form');
  const detailsWrapper = container.querySelector('[data-feedback-details]');
  const detailsInput = container.querySelector('#article-feedback-details');
  const emailInput = container.querySelector('#article-feedback-email');
  const reasonsHeading = container.querySelector('[data-feedback-reasons-heading]');
  const status = container.querySelector('#feedback-status');
  const submitButton = form?.querySelector('button[type="submit"]');
  if (!prompt || !yesButton || !noButton || !form || !status || !submitButton) return;

  const articleTitle = container.dataset.articleTitle || '';
  const articleUrl = container.dataset.articleUrl || '';

  function setSelected(vote) {
    for (const button of [yesButton, noButton]) {
      const active = button.dataset.feedbackVote === vote;
      button.classList.toggle('is-selected', active);
      button.setAttribute('aria-pressed', String(active));
    }
  }

  function revealReasons() {
    setSelected('no');
    form.removeAttribute('hidden');
    focusElement(reasonsHeading);
  }

  async function submitVote(payload) {
    setBusy(container, true);
    yesButton.disabled = true;
    noButton.disabled = true;
    if (submitButton) submitButton.disabled = true;
    status.textContent = container.dataset.savingLabel || '';

    try {
      const response = await submitFeedback(FEEDBACK_ENDPOINT, {
        articleId,
        articleTitle,
        articleUrl,
        ...payload,
      });
      if (!response.ok) throw new Error(`Feedback request returned HTTP ${response.status}`);
      await response.json().catch(() => ({}));
      storeVote(articleId, payload.vote);
      status.textContent = container.dataset.savedLabel || '';
      showThanks(container, thanksMessage);
    } catch (error) {
      console.error('[article-feedback]', error);
      status.textContent = container.dataset.errorLabel || '';
      focusElement(status);
      yesButton.disabled = false;
      noButton.disabled = false;
      if (submitButton) submitButton.disabled = false;
    } finally {
      setBusy(container, false);
    }
  }

  yesButton.addEventListener('click', () => {
    setSelected('yes');
    submitVote({ vote: 'yes' });
  });

  noButton.addEventListener('click', () => {
    if (!form.hasAttribute('hidden')) return;
    revealReasons();
  });

  // The reply email only belongs to "Something else": keep it disabled
  // otherwise so a leftover invalid address cannot block native form
  // validation for another reason.
  if (emailInput) emailInput.disabled = true;

  form.addEventListener('change', (event) => {
    if (event.target?.name !== 'feedback-reason') return;
    const showDetails = selectedReason(form) === SOMETHING_ELSE;
    detailsWrapper?.toggleAttribute('hidden', !showDetails);
    if (emailInput) emailInput.disabled = !showDetails;
  });

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    const reason = selectedReason(form);
    if (!reason) {
      status.textContent = container.dataset.validationLabel || '';
      focusElement(form.querySelector('input[name="feedback-reason"]'));
      return;
    }
    const somethingElse = reason === SOMETHING_ELSE;
    submitVote({
      vote: 'no',
      reason,
      details: detailsInput?.value.trim() || '',
      ...(somethingElse && emailInput?.value.trim() ? { email: emailInput.value.trim() } : {}),
    });
  });
}

if (typeof document !== 'undefined') {
  initializeArticleFeedback();
}
